import { XCircle } from 'lucide-react';
import React, { useState } from 'react';
import useLocale from '../../contexts/LocaleContext';
import { Button } from './Button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './Dialog';

interface RejectUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string) => Promise<void>;
  userName: string;
  isLoading?: boolean;
}

export const RejectUserDialog: React.FC<RejectUserDialogProps> = ({
  open,
  onOpenChange,
  onConfirm,
  userName,
  isLoading = false,
}) => {
  const { dictionary } = useLocale();
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    if (!reason.trim()) {
      setError(dictionary['users.reject_reason_required']);
      return;
    }
    try {
      await onConfirm(reason);
      // Only reset on success
      setReason('');
      setError('');
    } catch (error) {
      // Don't reset the form if there's an error
      // The error will be handled by the mutation's onError
    }
  };

  const handleClose = () => {
    setReason('');
    setError('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent showCloseButton={!isLoading}>
        <DialogHeader>
          <div className='flex items-start gap-4'>
            <div className='flex-shrink-0'>
              <XCircle className='h-6 w-6 text-red-600' />
            </div>
            <div className='flex-1'>
              <DialogTitle>{dictionary['users.reject_user']}</DialogTitle>
              <DialogDescription className='mt-2'>
                {dictionary['users.reject_user_description']} <span className='font-medium'>{userName}</span>
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className='space-y-4'>
          <div>
            <label htmlFor='reason' className='block text-sm font-medium text-gray-700 mb-2'>
              {dictionary['users.reject_reason_label']} <span className='text-red-600'>*</span>
            </label>
            <textarea
              id='reason'
              value={reason}
              onChange={e => {
                setReason(e.target.value);
                setError('');
              }}
              placeholder={dictionary['users.reject_reason_placeholder']}
              rows={4}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-red-400 resize-none ${
                error ? 'border-red-500' : ''
              }`}
              disabled={isLoading}
            />
            {error && <p className='mt-1 text-sm text-red-600'>{error}</p>}
          </div>
          <div className='bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded'>
            <p className='text-sm text-yellow-800'>{dictionary['users.reject_warning']}</p>
          </div>
        </div>

        <DialogFooter>
          <Button type='button' variant='outline' onClick={handleClose} disabled={isLoading}>
            {dictionary['common.cancel']}
          </Button>
          <Button type='button' onClick={handleConfirm} disabled={isLoading} className='bg-red-600 hover:bg-red-700'>
            {isLoading ? dictionary['users.rejecting'] : dictionary['users.reject_user']}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RejectUserDialog;
